import { useRef, useState } from 'react';

import { IconRefresh, IconX, IconZoomIn, IconZoomOut } from '@tabler/icons-react';
import { IconButton } from 'ui-kit';

interface Props {
  src: string;
  closeModal: () => void;
}

export const PhotoPreview = ({ src, closeModal }: Props) => {
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [zooming, setZooming] = useState('');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [start, setStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);

  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 10));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.3));
  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPosition({
      x: e.clientX - start.x,
      y: e.clientY - start.y,
    });
  };

  const handleMouseUp = () => setDragging(false);

  const handleWheel = (e: React.WheelEvent) => {
    if (e.deltaY < 0) {
      setZooming('+');
      // scroll up → zoom in
      setZoom((z) => Math.min(z + 0.25, 10));
    } else {
      setZooming('-');
      // scroll down → zoom out
      setZoom((z) => Math.max(z - 0.25, 0.3));
    }
  };
  return (
    <div className="relative flex flex-col items-center gap-4 p-4">
      {/* Controls */}
      <div className="absolute bottom-[-40px] left-1/2 z-10 flex -translate-x-1/2 gap-2 rounded bg-gray-800 p-1 opacity-50">
        <IconButton
          variant="icon-only"
          icon={<IconZoomIn color="#ffffff" width="20px" height="20px" />}
          onClick={zoomIn}
          className={'rounded !bg-none-50 p-2 active:!bg-gray-500'}
        />
        <IconButton
          variant="icon-only"
          icon={<IconZoomOut color="#ffffff" width="20px" height="20px" />}
          onClick={zoomOut}
          className={'rounded !bg-none-50 p-2 active:!bg-gray-500'}
        />
        <IconButton
          variant="icon-only"
          icon={<IconRefresh color="#ffffff" width="20px" height="20px" />}
          onClick={resetZoom}
          className={'rounded !bg-none-50 p-2 active:!bg-gray-500'}
        />

        <IconButton
          variant="icon-only"
          icon={<IconX color="#ffffff" width="20px" height="20px" />}
          onClick={closeModal}
          className={'rounded !bg-none-50 p-2 active:!bg-gray-500'}
        />
      </div>

      {/* Zoomable & draggable image */}
      <div
        className={`max-h-[80vh] max-w-full overflow-hidden ${
          dragging
            ? 'cursor-grabbing'
            : zooming === '+'
              ? 'cursor-zoom-in'
              : zooming === '-'
                ? 'cursor-zoom-out'
                : 'cursor-grab'
        }`}
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <img
          src={src}
          alt=""
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            transition: dragging ? 'none' : 'transform 0.2s',
            cursor: zoom > 1 ? 'grab' : 'default',
          }}
          className="pointer-events-none mx-auto max-h-[80vh] max-w-[80vw] select-none object-contain"
          draggable={false}
        />
      </div>
    </div>
  );
};
